import React from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {Button, TActionButtonOption} from '@edna/components';
import {Icon, Stack} from '@edna/components/primitives';

import {ButtonContent} from 'src/components/primitives';
import {CoinsIcon} from 'src/icons';

import {useGetPayKeeperAutopaymentInfoQuery} from '../../api';
import {usePayKeeperAutopaymentEnabled} from '../useAutopaymentEnabled';
import {showPayKeeperSettingsModal} from './PayKeeperSettingsModal';

const PaykeeperAutopaymentConnectButton = React.memo<{action?: TEmptyFunction | null}>(
  ({action}) => {
    const {t} = useTranslation();
    const {id: supportTenantId} = useParams<'id'>();

    const {data: info} = useGetPayKeeperAutopaymentInfoQuery();

    if (supportTenantId || info) {
      return null;
    }

    const handleChangeSettings = () => showPayKeeperSettingsModal(true);

    return (
      <Button onClick={action || handleChangeSettings} size="S" appearance="secondary">
        <ButtonContent size="1">
          <Icon as={CoinsIcon} size="16px" />
          {t('BalanceReplenishment:actions.connectAutopayment')}
        </ButtonContent>
      </Button>
    );
  },
);

const usePaykeeperAutopaymentButtonOption = (): TActionButtonOption | null => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  const isAutopaymentEnabled = usePayKeeperAutopaymentEnabled();

  const {data: info} = useGetPayKeeperAutopaymentInfoQuery(undefined, {
    skip: !isAutopaymentEnabled,
  });

  if (!isAutopaymentEnabled || supportTenantId) {
    return null;
  }

  return {
    content: (
      <Stack size="3" direction="ROW" align="center">
        <Icon as={CoinsIcon} size="20px" />
        {t(
          info
            ? 'BalanceReplenishment:actions.changeAutopaymentSettings'
            : 'BalanceReplenishment:actions.connectAutopayment',
        )}
      </Stack>
    ),
    handler: () => showPayKeeperSettingsModal(!info),
  };
};

export {PaykeeperAutopaymentConnectButton, usePaykeeperAutopaymentButtonOption};
